import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import Dashboard from "@/pages/Dashboard";
import AnalysisPage from "@/pages/AnalysisPage";
import ComparePage from "@/pages/ComparePage";
import BuilderPage from "@/pages/BuilderPage";
import CoverLetterPage from "@/pages/CoverLetterPage";
import GuidedResumeCreatorPage from "@/pages/GuidedResumeCreatorPage";
import PricingPage from "@/pages/PricingPage";
import AccountPage from "@/pages/AccountPage";
import AdminPage from "@/pages/AdminPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TailorResumePage from "@/pages/TailorResumePage";
import WhyResumeNoInterviewsPage from "@/pages/WhyResumeNoInterviewsPage";
import ResourcesIndexPage from "@/pages/ResourcesIndexPage";
import ResourcePage from "@/pages/ResourcePage";
import NotFoundPage from "@/pages/NotFoundPage";
import PageViewTracker from "@/components/PageViewTracker";
import { featureFlags } from "@/config/featureFlags";

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
function ProtectedRoute({ children }) {
  const { loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="text-center max-w-md p-8">
          <h2 className="font-heading text-2xl font-bold mb-4">Access Denied</h2>
          <p className="text-muted-foreground mb-6">
            This page is restricted to administrators only.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-hover transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return children;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/how-to-tailor-a-resume-to-a-job-description" element={<TailorResumePage />} />
      <Route path="/why-your-resume-is-not-getting-interviews" element={<WhyResumeNoInterviewsPage />} />
      <Route path="/resources" element={<ResourcesIndexPage />} />
      <Route path="/resources/:slug" element={<ResourcePage />} />

      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/analysis" element={
        <ProtectedRoute><AnalysisPage /></ProtectedRoute>
      } />
      <Route path="/compare" element={
        <ProtectedRoute><ComparePage /></ProtectedRoute>
      } />
      <Route path="/builder" element={
        <ProtectedRoute><BuilderPage /></ProtectedRoute>
      } />
      <Route path="/cover-letter" element={
        <ProtectedRoute><CoverLetterPage /></ProtectedRoute>
      } />
      <Route path="/guided-resume-creator" element={
        featureFlags.guidedResumeCreator ? (
          <ProtectedRoute><GuidedResumeCreatorPage /></ProtectedRoute>
        ) : (
          <ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>
        )
      } />
      <Route path="/account" element={
        <ProtectedRoute><AccountPage /></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute><AdminRoute><AdminPage /></AdminRoute></ProtectedRoute>
      } />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <PageViewTracker />
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
